import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import {
  formatCodeForDisplay,
  type CodeFormatLanguage,
} from "@/components/ui/sql-display/code-formatting";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import { useWorkflowNodeTypes } from "@/hooks/api/queries/workflows.queries";
import type { WorkflowStep } from "@/types/workflow.types";
import { configEditorLanguage, stringifyConfigValue } from "./structured-config-utils";
import {
  buildCatalogFields,
  catalogConfigValueForKey,
  initialCatalogValueForKey,
  isEmptyOptionalCatalogValue,
  parseCatalogFieldValue,
} from "./catalog-step-config-utils";
import { CatalogStepConfigField } from "./catalog-step-config-field";

interface CatalogStepConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

type SupportedFormatLanguage = Extract<CodeFormatLanguage, "javascript" | "typescript" | "json">;

interface FormatTarget {
  key: string;
  rawDraft: string;
  language: SupportedFormatLanguage;
}

export function CatalogStepConfigForm({ step, workflowId, onSuccess }: CatalogStepConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const { data: catalogData, isLoading, isError } = useWorkflowNodeTypes();
  const nodeType = catalogData?.data?.nodes.find((item) => item.type === step.stepType);
  const fields = useMemo(() => buildCatalogFields(nodeType, step.config), [nodeType, step.config]);
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      fields.map((field) => [field.key, initialValueForField(field, step.config)]),
    ),
  );
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((field) => [
        field.key,
        stringifyConfigValue(initialValueForField(field, step.config)),
      ]),
    ),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setValues((current) => {
      const next = { ...current };
      let changed = false;
      for (const field of fields) {
        if (!(field.key in next)) {
          next[field.key] = initialValueForField(field, step.config);
          changed = true;
        }
      }
      return changed ? next : current;
    });
    setDrafts((current) => {
      const next = { ...current };
      let changed = false;
      for (const field of fields) {
        if (!(field.key in next)) {
          next[field.key] = stringifyConfigValue(initialValueForField(field, step.config));
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [fields, step.config]);

  useEffect(() => {
    let cancelled = false;
    const formatTargets = fields
      .map((field) => {
        const value = initialValueForField(field, step.config);
        const rawDraft = stringifyConfigValue(value);
        const language = configEditorLanguage(field.key, value);
        return isFormatSupported(language) ? { key: field.key, rawDraft, language } : null;
      })
      .filter((item): item is FormatTarget => item !== null);

    if (formatTargets.length === 0) return undefined;

    Promise.all(
      formatTargets.map(async (target) => ({
        ...target,
        formatted: await formatCodeForDisplay(target.rawDraft, target.language),
      })),
    ).then((formattedTargets) => {
      if (cancelled) return;
      setDrafts((current) => {
        let changed = false;
        const next = { ...current };
        for (const target of formattedTargets) {
          if (target.formatted === target.rawDraft) continue;
          if (current[target.key] !== target.rawDraft) continue;
          next[target.key] = target.formatted;
          changed = true;
        }
        return changed ? next : current;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [fields, step.config]);

  const setValue = (key: string, value: unknown) => {
    setValues((current) => ({ ...current, [key]: value }));
    setDrafts((current) => ({ ...current, [key]: stringifyConfigValue(value) }));
    clearError(key, setErrors);
  };

  const setDraft = (key: string, value: string) => {
    setDrafts((current) => ({ ...current, [key]: value }));
    clearError(key, setErrors);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const payload: Record<string, unknown> = {};

    for (const field of fields) {
      try {
        const parsed = parseCatalogFieldValue(
          field.key,
          field.schema,
          values[field.key],
          drafts[field.key] ?? "",
        );
        if (field.required || !isEmptyOptionalCatalogValue(parsed)) {
          payload[field.key] = parsed;
        }
      } catch {
        nextErrors[field.key] = t("drawer.validation.invalid_json");
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    await updateStep.mutateAsync({ workflowId, stepId: step.id, config: payload });
    onSuccess?.();
  };

  if (isLoading) {
    return (
      <p className="text-sm text-[var(--muted-foreground)]">{t("drawer.config.loading_catalog")}</p>
    );
  }

  if (isError || !nodeType) {
    return (
      <p className="text-sm text-[var(--destructive)]">{t("drawer.config.catalog_unavailable")}</p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {fields.length === 0 && (
        <div className="rounded-lg border border-dashed border-[var(--border)] bg-[var(--muted)]/40 p-4 text-sm text-[var(--muted-foreground)]">
          {t("drawer.config.empty")}
        </div>
      )}

      {fields.map((field) => (
        <CatalogStepConfigField
          key={field.key}
          field={field}
          nodeType={step.stepType}
          value={values[field.key]}
          draft={drafts[field.key] ?? ""}
          error={errors[field.key]}
          setValue={setValue}
          setDraft={setDraft}
        />
      ))}

      {(Object.keys(errors).length > 0 || updateStep.isError) && (
        <p className="text-xs text-[var(--destructive)]">
          {Object.keys(errors).length > 0
            ? t("drawer.validation.fix_invalid_fields")
            : t("drawer.save_error")}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        size="sm"
        disabled={updateStep.isPending}
        className="w-full"
      >
        {updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
      </Button>
    </form>
  );
}

function clearError(key: string, setErrors: Dispatch<SetStateAction<Record<string, string>>>) {
  setErrors((current) => {
    const next = { ...current };
    delete next[key];
    return next;
  });
}

function initialValueForField(
  field: ReturnType<typeof buildCatalogFields>[number],
  config: Record<string, unknown>,
) {
  return initialCatalogValueForKey(
    field.key,
    catalogConfigValueForKey(field.key, config),
    field.schema,
  );
}

function isFormatSupported(language: string): language is SupportedFormatLanguage {
  return language === "javascript" || language === "typescript" || language === "json";
}

export default CatalogStepConfigForm;
